import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import VanGFowlerBolsContainer from '../../containers/billing/VanGFowlerBols';
import AuthUserContainer from '../../containers/AuthUser';

import { getSharedStyles } from '../../themes';
import EnhancedTable from '../../components/table/EnhancedTable';
import { Typography } from '@material-ui/core';

import ResourcePopup from '../../components/Dialogs/ResourcePopup';
import VanGFowlerBolUploadDialog from '../../components/Dialogs/VanGFowlerBolUploadDialog';
import { grey, blue } from '@material-ui/core/colors';
import BolLinkedStatus, { LINKED_STATUS_ENUM } from '../../components/billing/BolLinkedStatus';

import invoiceIcon from '../../images/invoicePdf.svg';

const UPLOAD_BOL_ENABLED = true;

const INTAKE_TYPE_ENUM = {
	manual: 'manual',
	emailIngestion: 'emailIngestion',
};

const intakeTypeToDisplayMap = {
	[INTAKE_TYPE_ENUM.manual]: 'Manual',
	[INTAKE_TYPE_ENUM.emailIngestion]: 'Email Ingestion',
};

const intakeTypeToColorMap = {
	[INTAKE_TYPE_ENUM.manual]: grey[500],
	[INTAKE_TYPE_ENUM.emailIngestion]: blue[500],
};

function intakeTypeToDisplay(intakeType) {
	return intakeTypeToDisplayMap[intakeType] || 'Manual';
}

const styles = (theme) => ({
	...getSharedStyles(theme),
	uploadButton: {
		borderRadius: 100,
	},
	invoiceIcon: {
		display: 'inline-block',
		width: 40,
		height: 48,
		backgroundImage: `url('${invoiceIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
});

const columns = [
	{
		sortId: 'vanGFowlerBolId',
		displayId: 'vanGFowlerBolId',
		label: 'ID',
	},
	{
		sortId: 'bolNumber',
		displayId: 'bolNumber',
		label: 'BOL #',
	},
	{
		sortId: 'railcarNumber1',
		displayId: 'railcarDisplay',
		label: 'Railcar #',
	},
	{
		sortId: 'poNumberRaw',
		displayId: 'poNumberRaw',
		label: 'PO # / Loading #',
	},
	{
		sortId: 'destinationStationNumber',
		displayId: 'destinationDisplay',
		label: 'Destination',
	},
	{
		sortId: 'carrierRaw',
		displayId: 'carrierDisplay',
		label: 'Carrier',
	},
	{
		sortId: 'startDateTime',
		displayId: 'shipDateDisplay',
		label: 'Ship Date',
	},
	{
		sortId: 'netGallons',
		displayId: 'netGallons',
		label: 'Gallons',
	},
	{
		noSort: true,
		sortId: 'bolPdfResourceId',
		displayId: 'bolPdfDisplay',
		label: 'PDF',
	},
	{
		sortId: 'intakeType',
		displayId: 'intakeDisplay',
		label: 'Intake',
	},
	{
		sortId: 'linkedStationDeliveryId',
		displayId: 'linkedStationDeliveryDisplay',
		label: 'Linked Delivery',
	},
	{
		sortId: 'linkedStatus',
		displayId: 'linkedStatusDisplay',
		label: 'Linked Status',
	},
];

class VanGFowlerBols extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'desc',
			orderBy: 'vanGFowlerBolId',
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
			isUploadOpen: false,
			reparsingVanGFowlerBolId: null,
		};
	}

	componentDidMount() {
		const { vanGFowlerBols } = this.props;

		if (!vanGFowlerBols) {
			this.refreshTable();
		} else {
			this.formatVanGFowlerBols();
		}
	}

	componentDidUpdate(prevProps) {
		const { vanGFowlerBols } = this.props;
		if (prevProps.vanGFowlerBols !== vanGFowlerBols) {
			this.formatVanGFowlerBols();
		}
	}

	refreshTable = () => {
		this.props.vanGFowlerBolSearch({
			afterDateTime: moment().subtract(2, 'months').format('YYYY-MM-DD'),
		});
	};

	formatVanGFowlerBols = () => {
		const { classes, vanGFowlerBols, stationMap, isDwp } = this.props;
		const { reparsingVanGFowlerBolId } = this.state;

		const rows = [];
		(vanGFowlerBols || []).forEach((vanGFowlerBol) => {
			const { vanGFowlerBolId, startDateTime, dateCreated } = vanGFowlerBol;

			vanGFowlerBol.key = vanGFowlerBolId;
			vanGFowlerBol.shipDateDisplay = startDateTime ? moment(startDateTime).format('M/D/YY') : '-';
			vanGFowlerBol.dateCreatedFormatted = dateCreated
				? moment(dateCreated).format('M/D/YY h:mm a')
				: '-';

			// A Fowler BOL can load from two railcars — show each railcar with its
			// per-railcar gallons on its own line.
			vanGFowlerBol.railcarDisplay = (
				<div>
					<div style={{ whiteSpace: 'nowrap' }}>
						{vanGFowlerBol.railcarNumber1 || '-'}
						{vanGFowlerBol.railcarNumber1 && !_.isNil(vanGFowlerBol.netGallons1)
							? ` (${vanGFowlerBol.netGallons1})`
							: ''}
					</div>
					{vanGFowlerBol.railcarNumber2 && (
						<div style={{ whiteSpace: 'nowrap' }}>
							{vanGFowlerBol.railcarNumber2}
							{!_.isNil(vanGFowlerBol.netGallons2) ? ` (${vanGFowlerBol.netGallons2})` : ''}
						</div>
					)}
				</div>
			);

			vanGFowlerBol.destinationDisplay =
				vanGFowlerBol.destinationStationNumber || vanGFowlerBol.destinationRaw || '-';

			vanGFowlerBol.carrierDisplay = vanGFowlerBol.carrierRaw || '-';

			vanGFowlerBol.bolPdfDisplay = vanGFowlerBol.bolPdfResourceId ? (
				<div
					className={classes.invoiceIcon}
					onClick={() => {
						this.handlePdfClick(vanGFowlerBol.bolPdfResourceId);
					}}
				/>
			) : (
				'-'
			);

			// DEV-only (dwp): re-run the parser + link on a row that failed under an
			// older parser. Rows that are already railcar-linked are refused server-side.
			const canReparse = isDwp && vanGFowlerBol.linkedStatus !== LINKED_STATUS_ENUM.railcarLinked;
			vanGFowlerBol.linkedStatusDisplay = (
				<div>
					<BolLinkedStatus
						linkedStatus={vanGFowlerBol.linkedStatus}
						message={vanGFowlerBol.linkedErrorMessage}
					/>
					{canReparse && (
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							style={{ marginTop: 6, whiteSpace: 'nowrap' }}
							disabled={reparsingVanGFowlerBolId === vanGFowlerBolId}
							onClick={() => {
								this.handleReparse(vanGFowlerBolId);
							}}
						>
							{reparsingVanGFowlerBolId === vanGFowlerBolId ? 'Reparsing...' : 'DEV Only: Reparse'}
						</Button>
					)}
				</div>
			);

			vanGFowlerBol.intakeDisplay = (
				<div>
					<div
						style={{ color: intakeTypeToColorMap[vanGFowlerBol.intakeType], fontWeight: 'bold' }}
					>
						Type: {intakeTypeToDisplay(vanGFowlerBol.intakeType)}
					</div>
					<div>Date Created: {vanGFowlerBol.dateCreatedFormatted}</div>
				</div>
			);

			if (vanGFowlerBol.linkedStationDeliveryId) {
				const station = stationMap[vanGFowlerBol.linkedStationId] || { number: 'Missing' };
				vanGFowlerBol.linkedStationDeliveryDisplay = (
					<div>
						<div style={{ whiteSpace: 'nowrap' }}>
							Station Delivery #
							<a
								href={`/logistics/stationDelivery/update/${vanGFowlerBol.linkedStationDeliveryId}`}
								style={{ textDecoration: 'none', paddingTop: 5, paddingLeft: 5 }}
								target="_blank"
								rel="noreferrer noopener"
							>
								<strong>{vanGFowlerBol.linkedStationDeliveryId}</strong>
							</a>
						</div>
						<div>
							Station: {station.number} / Tank: {vanGFowlerBol.linkedTankNumber}
						</div>
					</div>
				);
			}

			rows.push(vanGFowlerBol);
		});
		this.setState({ rows });
	};

	handleReparse = (vanGFowlerBolId) => {
		const { vanGFowlerBolReparse } = this.props;
		this.setState({ reparsingVanGFowlerBolId: vanGFowlerBolId }, this.formatVanGFowlerBols);
		vanGFowlerBolReparse({
			params: { vanGFowlerBolId },
			// apiFetch already surfaces errors via ERROR_EVENT; just refresh either
			// way so the row's new parse/link status shows.
			callback: () => {
				this.setState({ reparsingVanGFowlerBolId: null }, this.formatVanGFowlerBols);
				this.refreshTable();
			},
		});
	};

	handleOpenUpload = () => {
		this.setState({ isUploadOpen: true });
	};

	handleCloseUpload = () => {
		this.setState({ isUploadOpen: false });
	};

	handlePdfClick = (resourceId) => {
		this.setState({
			resourceIdsPoppedUp: [resourceId],
			isResourcePopupOpen: true,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	render() {
		const { classes, resourceMap, vanGFowlerBolUpload, showErrors } = this.props;
		const { rows, order, orderBy } = this.state;

		if (!rows) {
			return <div>Loading the Van G Fowler BOLs...</div>;
		}

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={8}>
							<Typography variant="h5" align="left" gutterBottom className={classes.title}>
								<span>Van G Fowler BOLs</span>
							</Typography>
						</Grid>
						<Grid item xs={4} style={{ textAlign: 'right' }}>
							<Button
								variant="outlined"
								color="primary"
								className={classes.uploadButton}
								style={{ marginRight: 10 }}
								onClick={this.refreshTable}
							>
								Refresh
							</Button>
							{UPLOAD_BOL_ENABLED && (
								<Button
									variant="contained"
									color="primary"
									className={classes.uploadButton}
									onClick={this.handleOpenUpload}
								>
									Upload BOL
								</Button>
							)}
						</Grid>
					</Grid>

					<Paper className={classes.paper}>
						<EnhancedTable
							columns={columns}
							rows={rows}
							defaultOrder={order}
							defaultOrderBy={orderBy}
						/>

						<ResourcePopup
							open={this.state.isResourcePopupOpen}
							onClose={this.handleCloseResourcePopup}
							resourceIds={this.state.resourceIdsPoppedUp}
							resourceMap={resourceMap}
						/>
					</Paper>

					{UPLOAD_BOL_ENABLED && (
						<VanGFowlerBolUploadDialog
							open={this.state.isUploadOpen}
							onClose={this.handleCloseUpload}
							uploadBol={vanGFowlerBolUpload}
							showErrors={showErrors}
							onAfterSave={this.refreshTable}
						/>
					)}
				</Grid>
			</Grid>
		);
	}
}

VanGFowlerBols.propTypes = {
	classes: PropTypes.object.isRequired,
	vanGFowlerBolSearch: PropTypes.func.isRequired,
	vanGFowlerBolUpload: PropTypes.func.isRequired,
	vanGFowlerBolReparse: PropTypes.func.isRequired,
	vanGFowlerBols: PropTypes.array,
	isDwp: PropTypes.bool,

	showErrors: PropTypes.func.isRequired,
	resourceMap: PropTypes.object,
	stationMap: PropTypes.object,
	terminalMap: PropTypes.object,

	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
	authUser: PropTypes.object,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	VanGFowlerBolsContainer,
])(VanGFowlerBols);
